import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { ChipColorStyles } from "_interfaces/_common";
import {
  CheckoutBillInterface,
  FETCH_PAYMENT_TIMEOUT_MINUTES,
  PaymentMethodDataProps,
} from "_interfaces/_common/payment";
import {
  CouponApplyOnEnum,
  CouponCheckApplicabilityProps,
} from "_interfaces/http-requests/coupon";
import { BundleRechargeRequestProps } from "_interfaces/http-requests/recharge";
import { BundlePlansFiltersProps } from "_interfaces/products/bundle-plans";
import { ApplyCouponModel } from "_models/data/payment/data.applyCoupon.model";
import { WalletPaymentModel } from "_models/data/wallet/data.walletPayment.model";
import { ApplyCouponErrorModel } from "_models/errors/payment/error.applyCoupon.model";
import PaymentCheckout from "component/_common/payment/paymentCheckout";
import { paymentOptionsList } from "component/_common/payment/paymentOptionsList";
import WalletPaymentMethod from "component/_common/payment/walletPaymentMethod";
import PlanCardFour from "component/_common/plans/planCardFour";
import RechargeNumberLayout from "component/_common/recharge/rechargeNumberLayout";
import useProcessStatusModal from "component/_common/status/useProcessStatusModal";
import { AppStatusCode } from "config/appStatusCode";
import { formatPriceWithDelimiter } from "functions/helper";
import { HTTP_ERROR } from "functions/http";
import { CouponCheckApplicability } from "functions/http-requests/coupon";
import {
  RechargeBundle,
  RechargeFulfill,
} from "functions/http-requests/recharge";
import RouteConstant from "navigation/constant";
import SectionTitle from "parts/sectionTitle";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAlert } from "state/slice/alert";
import { toggleLoading } from "state/slice/loading";
import { DispatchRechargeData } from "state/slice/rechargePlan";
import { RootState } from "state/store";
import AllBundlePlans from "./allBundlePlans";
import AllBundlePlansFilter from "./allBundlePlansFilter";

const BundlePlansLayout = () => {
  const Dispatch = useDispatch();
  const navigate = useNavigate();
  const { palette } = useTheme();

  const { isLoggedIn, userInfo } = useSelector(
    (state: RootState) => state.user,
  );
  const { rechargeData } = useSelector(
    (state: RootState) => state.rechargePlan,
  );

  const [filters, setFilters] = useState<BundlePlansFiltersProps>({
    packageFor: "normal",
    packageType: undefined,
    packageValidity: undefined,
    unlimited: true,
  });

  const [openRechargeNumberLayout, setOpenRechargeNumberLayout] =
    useState<boolean>(false);

  const [openPaymentOption, setOpenPaymentOption] = useState<boolean>(false);
  const [openWalletPaymentMethod, setOpenWalletPaymentMethod] =
    useState<boolean>(false);

  const [activeRechargeId, setActiveRechargeId] = useState<string>("");
  const [processingSnapshot, setProcessingSnapshot] = useState<{
    phoneNumber?: string;
    amount?: number;
    paymentBy?: string;
    paymentMethod?: string;
    packageBenefit?: string;
    couponCode?: string;
  }>({});
  const [processFailedMessage, setProcessFailedMessage] = useState<string>("");
  const DEFAULT_BUNDLE_RECHARGE_FAILED_MESSAGE =
    "We could not confirm your recharge right now. Please wait a few minutes and try again. If you were charged, it may still be completed automatically.";

  const RECHARGE_POLL_SECONDS = 10;
  const RECHARGE_POLL_TIMEOUT_SECONDS = FETCH_PAYMENT_TIMEOUT_MINUTES * 60;

  const [walletPaymentState, setWalletPaymentState] =
    useState<WalletPaymentModel>(new WalletPaymentModel());

  const [couponState, setCouponState] = useState<ApplyCouponModel>(
    new ApplyCouponModel(),
  );
  const [couponErrors, setCouponErrors] = useState<ApplyCouponErrorModel>(
    new ApplyCouponErrorModel(),
  );

  const handleToggleRechargeNumberLayout = () =>
    setOpenRechargeNumberLayout(!openRechargeNumberLayout);

  const handleTogglePaymentOption = () =>
    setOpenPaymentOption(!openPaymentOption);

  const handleToggleWalletPaymentMethod = () =>
    setOpenWalletPaymentMethod(!openWalletPaymentMethod);

  const bundleRechargeProcess = useProcessStatusModal({
    timeoutSeconds: RECHARGE_POLL_TIMEOUT_SECONDS,
    processingGuardMessage:
      "Recharge is being processed. Do not refresh, close, or go back.",
  });
  const {
    open: processOpen,
    status: processStatus,
    countdownLabel: processCountdownLabel,
    startProcessing: startProcessUi,
    setFailed: setProcessFailed,
    setDelayed: setProcessDelayed,
    close: closeProcessUi,
  } = bundleRechargeProcess;

  const beginCheckoutProcessUi = useCallback(() => {
    setOpenWalletPaymentMethod(false);
    startProcessUi();
  }, [startProcessUi]);

  const resolveFailedMessage = useCallback(
    (message?: string) => message || DEFAULT_BUNDLE_RECHARGE_FAILED_MESSAGE,
    [],
  );

  const handleCloseAllBundlePlansModals = useCallback(() => {
    closeProcessUi();
    setActiveRechargeId("");
    setProcessingSnapshot({});
    setOpenWalletPaymentMethod(false);
    setWalletPaymentState(new WalletPaymentModel());
    Dispatch(toggleLoading(false));
    setCouponState(new ApplyCouponModel());
    setCouponErrors(new ApplyCouponErrorModel());
    setOpenPaymentOption(false);
    setOpenRechargeNumberLayout(false);
    Dispatch(DispatchRechargeData({ data: null }));
  }, [closeProcessUi, Dispatch]);

  const rechargeDetails = useMemo(() => {
    return [
      {
        label: "Phone Number",
        value:
          processingSnapshot.phoneNumber ||
          rechargeData?.phoneNumber ||
          userInfo?.phone ||
          "-",
      },
      {
        label: "Amount",
        value:
          typeof processingSnapshot.amount === "number"
            ? `UGX ${formatPriceWithDelimiter(processingSnapshot.amount)}`
            : "-",
      },
      {
        label: "Plan Details",
        value: processingSnapshot.packageBenefit || "-",
        fullWidth: true,
      },
    ];
  }, [processingSnapshot, rechargeData?.phoneNumber, userInfo?.phone]);

  const bundlePlansEmbeddedPaymentProcess = useMemo(() => {
    if (!processOpen) return null;
    const paidWithWallet = processingSnapshot.paymentBy === "wallet";

    return {
      status: processStatus,
      processingTitle: "Recharge is being processed",
      successTitle: "Recharge successful",
      failedTitle: "Recharge failed",
      delayedTitle: "Recharge confirmation pending",
      processingMessage: paidWithWallet
        ? "Please do not press back, close this window, or refresh the page while we confirm your wallet payment and complete your recharge."
        : "Please do not press back, close this window, or refresh the page while we confirm your recharge.",
      successMessage: "Your recharge has been completed successfully.",
      failedMessage:
        processFailedMessage || DEFAULT_BUNDLE_RECHARGE_FAILED_MESSAGE,
      delayedMessage:
        "This recharge is still being processed. Please wait up to 30 minutes. Your recharge will be completed automatically once the payment gateway confirms it. If it is still not updated after 30 minutes, please contact support.",
      processingWarningMessage: paidWithWallet
        ? "Please do not press back, close this window, or refresh the page while we confirm your wallet payment and complete your recharge."
        : "Please do not press back, close this window, or refresh the page while we confirm your recharge.",
      details: rechargeDetails,
      onPrimaryAction:
        processStatus === "failed" || processStatus === "delayed"
          ? () => {
              handleCloseAllBundlePlansModals();
              navigate(RouteConstant.CONTACT_US);
            }
          : undefined,
      primaryActionLabel: "Contact Support",
      onClose: handleCloseAllBundlePlansModals,
    };
  }, [
    processOpen,
    processStatus,
    processFailedMessage,
    processingSnapshot.paymentBy,
    processCountdownLabel,
    rechargeDetails,
    DEFAULT_BUNDLE_RECHARGE_FAILED_MESSAGE,
    handleCloseAllBundlePlansModals,
    navigate,
  ]);

  const handleApplyCoupon = (DATA: ApplyCouponModel) => {
    if (!DATA?.coupon || !rechargeData?.data?.retailPrice) {
      return Dispatch(
        setAlert({ message: `Mandatory data missing!`, type: "error" }),
      );
    }
    const PAYLOAD_DATA: CouponCheckApplicabilityProps["DATA"] = {
      couponCode: DATA.coupon,
      amount: rechargeData.data?.retailPrice || 0,
      applyOn: CouponApplyOnEnum.recharge,
    };
    Dispatch(toggleLoading(true));
    CouponCheckApplicability({ DATA: PAYLOAD_DATA })
      .then((res) => {
        const data = res?.data;
        if (
          data?.statusCode === AppStatusCode.api_success &&
          data?.data?.isApplicable
        ) {
          setCouponState((prev) => ({
            ...prev,
            ...(data?.data || {}),
          }));
        } else {
          Dispatch(
            setAlert({
              type: "error",
              message: data?.data?.reason || "Something went wrong!",
            }),
          );
          setCouponState(() => ({
            coupon: "",
            isApplied: false,
          }));
        }
      })
      .catch((error) => {
        Dispatch(setAlert({ type: "error", message: HTTP_ERROR(error) }));
      })
      .finally(() => Dispatch(toggleLoading(false)));
  };

  const handleRemoveCoupon = () => {
    setCouponState(new ApplyCouponModel());
  };

  let handleRechargeRequest = (DATA: PaymentMethodDataProps) => {
    if (
      DATA?.paymentBy &&
      DATA?.paymentMethod &&
      rechargeData?.data?._id &&
      rechargeData?.phoneNumber
    ) {
      let PAYLOAD_DATA: BundleRechargeRequestProps["DATA"] = {
        bundleId: rechargeData.data._id,
        phoneNumber: rechargeData.phoneNumber,
        paymentMethod: DATA.paymentMethod,
        paymentBy: DATA.paymentBy,
        momoPhoneNumber: DATA.momoPhoneNumber || undefined,
        airtelMOMOPhone: DATA.airtelMOMOPhone || undefined,
        couponCode:
          couponState?.isApplicable && couponState?.coupon
            ? couponState.coupon
            : undefined,
      };

      setProcessingSnapshot({
        phoneNumber: rechargeData?.phoneNumber,
        amount: Number(
          (couponState?.isApplicable && couponState?.finalAmount) ||
            rechargeData?.data?.retailPrice ||
            0,
        ),
        paymentBy: DATA.paymentBy,
        paymentMethod: DATA.paymentMethod,
        packageBenefit: [
          rechargeData?.data?.dataVolume?.quantity &&
          rechargeData?.data?.dataVolume?.dataUnit
            ? `Data: ${formatPriceWithDelimiter(
                Number(rechargeData.data.dataVolume.quantity),
              )} ${rechargeData.data.dataVolume.dataUnit}`
            : null,
          rechargeData?.data?.validity
            ? `Validity: ${rechargeData?.data?.validity} day(s)`
            : null,
        ]
          .filter(Boolean)
          .join(" | "),
        couponCode:
          couponState?.isApplicable && couponState?.coupon
            ? couponState.coupon
            : undefined,
      });

      if (DATA.paymentBy !== "wallet") {
        setProcessFailedMessage("");
        beginCheckoutProcessUi();
        setActiveRechargeId("");
      } else {
        Dispatch(toggleLoading(true));
      }

      RechargeBundle({ DATA: PAYLOAD_DATA })
        .then((res) => {
          const data = res?.data;
          const rechargeId = data?.data?.rechargeId;

          if (data?.statusCode === AppStatusCode.api_success && rechargeId) {
            if (DATA.paymentBy === "wallet") {
              const walletPaymentId = data?.data?.walletRequestId;
              setWalletPaymentState((prev) => ({
                ...prev,
                walletPaymentId: walletPaymentId,
                rechargeId: rechargeId,
              }));
              handleToggleWalletPaymentMethod();
              Dispatch(toggleLoading(false));
            } else {
              setActiveRechargeId(rechargeId);
            }
          } else {
            if (DATA.paymentBy !== "wallet") {
              setProcessFailedMessage(resolveFailedMessage(data?.message));
              setProcessFailed();
            } else {
              Dispatch(
                setAlert({
                  type: data?.level,
                  message: data?.message || "Something went wrong!",
                }),
              );
              Dispatch(toggleLoading(false));
            }
          }
        })
        .catch((error) => {
          Dispatch(setAlert({ type: "error", message: HTTP_ERROR(error) }));
          setProcessFailedMessage(resolveFailedMessage(HTTP_ERROR(error)));
          if (DATA.paymentBy !== "wallet") {
            setProcessFailed();
          }
          Dispatch(toggleLoading(false));
        });
    }
  };

  const handleRechargeRequestUpdate = (ID: string) => {
    Dispatch(toggleLoading(false));
    setActiveRechargeId(ID);
    beginCheckoutProcessUi();
  };

  useEffect(() => {
    if (!processOpen || processStatus !== "processing" || !activeRechargeId)
      return;

    let intervalId: NodeJS.Timeout;
    let timeoutId: NodeJS.Timeout;
    let stopped = false;

    const stop = () => {
      stopped = true;
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };

    const poll = () => {
      if (stopped) return;

      RechargeFulfill(activeRechargeId)
        .then((res) => {
          const data = res?.data;

          if (data?.statusCode === AppStatusCode.api_success) {
            const STATUS = data?.data?.status?.toLowerCase();
            if (STATUS === "success") {
              stop();
              Dispatch(
                setAlert({
                  type: data?.level,
                  message: data?.message,
                }),
              );
              handleCloseAllBundlePlansModals();
              navigate(RouteConstant.MY_ACCOUNT);
            } else if (STATUS === "pending") {
              // keep polling
            } else {
              setProcessFailedMessage(resolveFailedMessage(data?.message));
              setProcessFailed();
              stop();
            }
          } else {
            setProcessFailedMessage(resolveFailedMessage(data?.message));
            setProcessFailed();
            stop();
          }
        })
        .catch((error) => {
          setProcessFailedMessage(resolveFailedMessage(HTTP_ERROR(error)));
          setProcessFailed();
          stop();
        });
    };

    poll();
    intervalId = setInterval(poll, RECHARGE_POLL_SECONDS * 1000);
    timeoutId = setTimeout(() => {
      stop();
      setProcessDelayed();
    }, RECHARGE_POLL_TIMEOUT_SECONDS * 1000);

    return () => stop();
  }, [
    processOpen,
    processStatus,
    activeRechargeId,
    Dispatch,
    RECHARGE_POLL_TIMEOUT_SECONDS,
    setProcessFailed,
    setProcessDelayed,
    resolveFailedMessage,
    handleCloseAllBundlePlansModals,
    navigate,
  ]);

  useEffect(() => {
    if (userInfo.phone) {
      Dispatch(DispatchRechargeData({ phoneNumber: userInfo.phone }));
    }
  }, [userInfo?.phone, Dispatch]);

  const planCheckoutBill: CheckoutBillInterface[] = [
    {
      label: "",
      component: (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {rechargeData?.data?.retailPrice ? (
            <Box>
              <Typography
                fontSize={24}
                fontWeight={800}
                sx={{
                  textDecoration: couponState?.isApplicable
                    ? "line-through"
                    : "none",
                  color: couponState?.isApplicable
                    ? palette.error.main
                    : "inherit",
                }}
              >
                UGX{" "}
                {formatPriceWithDelimiter(rechargeData?.data?.retailPrice || 0)}
              </Typography>
              {couponState?.isApplicable &&
              couponState?.potentialDiscount &&
              couponState?.finalAmount ? (
                <>
                  <Typography fontSize={12} fontWeight={500}>
                    Discount Applied{" "}
                    {couponState?.couponInfo?.title
                      ? `(${couponState?.couponInfo?.title})`
                      : null}
                    : -UGX{" "}
                    {formatPriceWithDelimiter(couponState?.potentialDiscount)}
                  </Typography>
                  <Typography fontSize={20} fontWeight={600}>
                    Total Payable: UGX{" "}
                    {formatPriceWithDelimiter(couponState?.finalAmount)}
                  </Typography>
                </>
              ) : null}
            </Box>
          ) : null}

          {Array.isArray(rechargeData?.data?.packageType) ? (
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                gap: 1,
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              {rechargeData?.data?.packageType?.map((type, index) => {
                const { bg, color } =
                  ChipColorStyles[index % ChipColorStyles.length];
                return (
                  <Chip
                    key={type}
                    label={type.toUpperCase()}
                    size="small"
                    sx={{
                      backgroundColor: bg,
                      color: color,
                      fontWeight: 500,
                      flexShrink: 0,
                    }}
                  />
                );
              })}
            </Box>
          ) : null}

          {rechargeData?.data?._id ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography fontSize={14} fontWeight={500}>
                Plan Details
              </Typography>
              <Box
                sx={{
                  bgcolor: palette.grey[100],
                  p: 1,
                  borderRadius: 2,
                }}
              >
                <PlanCardFour data={rechargeData?.data} showOnlyPlanDetails />
              </Box>
            </Box>
          ) : null}
        </Box>
      ),
      enabled: Boolean(rechargeData?.data),
    },
  ];

  return (
    <>
      <Box maxWidth={600} id="next-step-for-package-bundle-selection">
        <SectionTitle
          title="Bundle Plans"
          subTitle="Choose from daily, weekly, or monthly periods of validity and
                enjoy both voice and data"
        />
      </Box>

      <Box pt={3}>
        <AllBundlePlansFilter filters={filters} setFilters={setFilters} />
      </Box>

      <AllBundlePlans
        filters={filters}
        handleSelectPlan={() => {
          handleToggleRechargeNumberLayout();
        }}
      />

      {/* PAYMENT METHOD */}
      <Dialog open={openPaymentOption} maxWidth="md" fullWidth>
        <PaymentCheckout
          checkoutTitle={
            rechargeData?.phoneNumber
              ? `Recharge ${rechargeData?.phoneNumber}`
              : ""
          }
          checkoutBill={openPaymentOption ? planCheckoutBill : []}
          handleMakePaymentRequest={handleRechargeRequest}
          togglePaymentCheckout={handleTogglePaymentOption}
          paymentOptions={
            isLoggedIn
              ? paymentOptionsList
              : paymentOptionsList.filter((item) => item.paymentBy !== "wallet")
          }
          isCouponFormEnabled
          couponState={couponState}
          setCouponState={setCouponState}
          couponErrors={couponErrors}
          setCouponErrors={setCouponErrors}
          handleApplyCoupon={handleApplyCoupon}
          handleRemoveCoupon={handleRemoveCoupon}
          embeddedPaymentProcess={bundlePlansEmbeddedPaymentProcess}
        />
      </Dialog>

      {/* WALLET PAYMENT METHOD */}
      <Dialog open={openWalletPaymentMethod} fullWidth maxWidth="sm">
        <WalletPaymentMethod
          handleClose={handleToggleWalletPaymentMethod}
          walletPaymentState={walletPaymentState}
          handleContinue={handleRechargeRequestUpdate}
        />
      </Dialog>

      {/* Recharge Number */}
      <Dialog
        open={openRechargeNumberLayout}
        onClose={handleToggleRechargeNumberLayout}
        maxWidth="sm"
        fullWidth
      >
        <RechargeNumberLayout
          onClose={handleToggleRechargeNumberLayout}
          handleContinue={() => {
            if (rechargeData?.data?._id) {
              handleTogglePaymentOption();
            }
          }}
        />
      </Dialog>
    </>
  );
};

export default BundlePlansLayout;
